import { useQuery } from '@tanstack/react-query';
import { DataTable, EmptyState, Money, StatusBadge, enumLabel, type Column } from '@riskpay/ui-components';
import type { Money as MoneyType, Settlement } from '@riskpay/types';
import { api } from '../lib/client';

/* ==========================================================================
 *  Your terms
 *
 *  What we charge and when we pay. Every one of these numbers was already
 *  being applied to this merchant's money on every transaction; none of them
 *  was ever shown. The only way to learn your own MDR was to divide a fee by
 *  an amount and guess at the rounding, which is not a reasonable thing to ask
 *  of somebody who is being charged it.
 *
 *  Read-only on purpose. Rates are commercial terms set by staff — a merchant
 *  who disagrees with one needs a conversation, not a form field.
 * ========================================================================== */

interface Pricing {
  currency: string;
  mdr: {
    methods: { method: string; percent: string; flat: MoneyType; example_on_1000: MoneyType; is_default: boolean }[];
    default_percent: string;
    gst_percent: string;
    note: string;
  };
  settlement: { cycle: string; label: string; detail: string; rail: string; payout_fee: MoneyType };
  reserve: { percent: string; days: number; note: string } | null;
  limits: { min: MoneyType; max: MoneyType };
}

function YourTerms() {
  const { data, isLoading } = useQuery({
    queryKey: ['pricing'],
    queryFn: () => api.get<Pricing>('/pricing'),
    // Commercial terms change when somebody negotiates, not by the minute.
    staleTime: 5 * 60_000,
  });

  if (isLoading || !data) return null;

  return (
    <section className="panel">
      <header className="border-b border-line px-5 py-3.5">
        <h2 className="text-base font-medium">Your terms</h2>
        <p className="mt-0.5 text-xs text-content-subtle">
          Set by RiskPay. Contact support to discuss a change.
        </p>
      </header>

      <div className="grid grid-cols-1 divide-y divide-line lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] lg:divide-x lg:divide-y-0">
        {/* Rates, per method, with what a real sale actually costs. */}
        <div className="px-5 py-4">
          <p className="meta">Transaction fee</p>
          <table className="mt-3 w-full">
            <thead>
              <tr className="text-2xs uppercase tracking-wider text-content-faint">
                <th className="pb-2 text-left font-medium">Method</th>
                <th className="pb-2 text-right font-medium">Rate</th>
                <th className="pb-2 text-right font-medium">On a ₹1,000 sale</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line-subtle">
              {data.mdr.methods.map((m) => (
                <tr key={m.method}>
                  <td className="py-2 text-xs text-content">{enumLabel(m.method)}</td>
                  <td className="numeral py-2 text-right text-xs text-content-muted">
                    {m.percent}%
                    {m.flat.amount !== '0.00' && <span className="text-content-faint"> + {m.flat.formatted}</span>}
                  </td>
                  <td className="numeral py-2 text-right text-xs text-content-muted">
                    {m.example_on_1000.formatted}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-3 text-2xs leading-relaxed text-content-faint">
            Plus {data.mdr.gst_percent}% GST on the fee — included in the column above. GST applies to our fee, not
            to the payment.
          </p>
        </div>

        {/* When the money lands, and anything held back. */}
        <div className="divide-y divide-line">
          <div className="px-5 py-4">
            <p className="meta">Settlement</p>
            <p className="mt-2 text-lg font-medium tracking-tight text-content">{data.settlement.label}</p>
            <p className="mt-1 text-xs leading-relaxed text-content-subtle">{data.settlement.detail}</p>
            <p className="mt-2 text-2xs text-content-faint">
              Paid to your {data.settlement.rail === 'CRYPTO' ? 'crypto wallet' : 'bank account'} · payout fee{' '}
              <span className="numeral">{data.settlement.payout_fee.formatted}</span>
            </p>
          </div>

          {data.reserve && (
            <div className="px-5 py-4">
              <p className="meta">Rolling reserve</p>
              <p className="numeral mt-2 text-lg font-medium tracking-tight text-content">
                {data.reserve.percent}% · {data.reserve.days} days
              </p>
              <p className="mt-1 text-xs leading-relaxed text-content-subtle">{data.reserve.note}</p>
            </div>
          )}

          <div className="px-5 py-4">
            <p className="meta">Per-payment limits</p>
            <p className="numeral mt-2 text-sm text-content-muted">
              {data.limits.min.formatted} – {data.limits.max.formatted}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * Settlements.
 *
 * A settlement row is what a finance team reconciles against the bank
 * statement, so the UTR and the frozen bank-account snapshot are first-class
 * columns rather than detail-view trivia.
 */
export function Settlements() {
  const { data, isLoading } = useQuery({
    queryKey: ['settlements'],
    queryFn: () => api.get<{ data: Settlement[] }>('/settlements'),
  });

  const columns: Column<Settlement>[] = [
    {
      key: 'id',
      header: 'Settlement',
      render: (s) => (
        <div>
          <p className="numeral text-xs text-content">{s.id}</p>
          <p className="mt-0.5 text-2xs text-content-faint">
            {s.cycle} · {s.transaction_count} transactions
          </p>
        </div>
      ),
    },
    { key: 'gross', header: 'Gross', align: 'right', render: (s) => <Money amount={s.gross.amount} className="text-xs" /> },
    { key: 'fees', header: 'Fees', align: 'right', render: (s) => <Money amount={s.fees.amount} className="text-xs text-content-subtle" /> },
    {
      key: 'reserve',
      header: 'Reserve',
      align: 'right',
      render: (s) => <Money amount={s.reserve_held.amount} className="text-xs text-content-subtle" />,
    },
    {
      key: 'net',
      header: 'Net payout',
      align: 'right',
      render: (s) => <Money amount={s.net_payout.amount} className="text-xs font-medium text-brand-400" />,
    },
    { key: 'status', header: 'Status', render: (s) => <StatusBadge status={s.status} /> },
    {
      key: 'utr',
      header: 'UTR',
      render: (s) =>
        s.utr ? (
          <span className="numeral text-xs text-content-muted">{s.utr}</span>
        ) : (
          <span className="text-2xs text-content-faint">pending</span>
        ),
    },
    {
      key: 'bank',
      header: 'To account',
      render: (s) => (
        <span className="numeral text-xs text-content-subtle">
          {s.bank_account?.bank} ••••{s.bank_account?.last4}
        </span>
      ),
    },
    {
      key: 'date',
      header: 'Date',
      align: 'right',
      render: (s) => (
        <span className="text-xs text-content-subtle">
          {new Date(s.paid_at ?? s.scheduled_for).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: '2-digit',
          })}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <YourTerms />

      <p className="text-xs text-content-muted">
        Settlements run automatically on your agreed cycle. Each row is one bank transfer — match it to your statement
        using the UTR.
      </p>
      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(s) => s.id}
        loading={isLoading}
        empty={
          <EmptyState
            title="No settlements yet"
            description="Your first settlement runs once captured volume ages past your settlement cycle."
          />
        }
      />
    </div>
  );
}
